import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ProgressBar } from './ProgressBar';
import { AssessmentQuestionCard } from './AssessmentQuestionCard';
import { IntroForm } from './IntroForm';
import { AssessmentComplete } from './AssessmentComplete';
import { TokenEntry } from './TokenEntry';
import { useAssessment } from '@/hooks/useAssessment';
import { useTokenValidation } from '@/hooks/useTokenValidation';
import { ArrowLeft, Pause, Loader2 } from 'lucide-react';
import { TOTAL_QUESTIONS } from '@/lib/questions';

export function AssessmentFlow() {
  const navigate = useNavigate();
  const {
    assessment,
    sessionSecret,
    responses,
    currentIndex,
    loading,
    saving,
    error,
    progressPercentage,
    currentQuestionId,
    currentQuestionText,
    isComplete,
    totalQuestions,
    startAssessment,
    saveResponse,
    goToPrevious,
    completeAssessment,
    estimatedTimeRemaining,
  } = useAssessment();

  const { redeemToken } = useTokenValidation();

  const [showComplete, setShowComplete] = useState(false);
  const [computing, setComputing] = useState(false);
  const [completedAssessmentId, setCompletedAssessmentId] = useState<string | null>(null);
  const [tokenCode, setTokenCode] = useState<string | null>(null);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [currentSessionSecret, setCurrentSessionSecret] = useState<string | null>(null);

  // Handle completion
  useEffect(() => {
    if (isComplete && assessment && !showComplete) {
      handleComplete();
    }
  }, [isComplete, assessment, showComplete]);

  // Track session secret changes
  useEffect(() => {
    if (sessionSecret) {
      setCurrentSessionSecret(sessionSecret);
    }
  }, [sessionSecret]);

  const handleComplete = async () => {
    setComputing(true);
    const assessmentId = await completeAssessment();
    if (assessmentId && currentSessionSecret) {
      setCompletedAssessmentId(assessmentId);
      
      setTimeout(() => {
        setComputing(false);
        setShowComplete(true);
      }, 2000);
    }
  };

  const handleTokenValidated = (code: string) => {
    setTokenCode(code);
    setTokenValidated(true);
  };

  const handleStart = async (info: {
    name: string;
    email: string;
    role?: string;
    practice_name?: string;
  }) => {
    const result = await startAssessment(info);
    
    // Redeem the token for this assessment
    if (result && tokenCode) {
      setCurrentSessionSecret(result.sessionSecret);
      await redeemToken(tokenCode, result.id, info.email, result.sessionSecret);
    }
  };

  const handleResponse = async (value: number) => {
    if (currentQuestionId) {
      await saveResponse(currentQuestionId, value);
    }
  };

  const handleResumeLater = () => {
    navigate('/');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="card-premium max-w-md text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  // Complete state
  if (showComplete && completedAssessmentId) {
    return <AssessmentComplete sessionId={completedAssessmentId} computing={computing} />;
  }

  // Computing state
  if (computing) {
    return <AssessmentComplete sessionId={assessment?.id || ''} computing={true} />;
  }

  // Token entry (no valid token yet)
  if (!tokenValidated && !assessment) {
    return <TokenEntry onValidToken={handleTokenValidated} />;
  }

  // Intro form (token validated but no assessment yet)
  if (!assessment) {
    return (
      <IntroForm
        onStart={handleStart}
        loading={saving}
        totalQuestions={TOTAL_QUESTIONS}
      />
    );
  }

  // Assessment flow
  const currentValue = currentQuestionId 
    ? responses.get(currentQuestionId) ?? null 
    : null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress bar */}
      <ProgressBar
        percentage={progressPercentage}
        currentQuestion={currentIndex}
        totalQuestions={totalQuestions}
        estimatedMinutes={estimatedTimeRemaining()}
        className="mb-8"
      />

      {/* Navigation */}
      <div className="flex justify-between items-center mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={goToPrevious}
          disabled={currentIndex === 0 || saving}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Previous
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResumeLater}
          className="gap-2 text-muted-foreground"
        >
          <Pause className="h-4 w-4" />
          Resume Later
        </Button>
      </div>

      {/* Current question */}
      {currentQuestionId && currentQuestionText && (
        <AssessmentQuestionCard
          key={currentQuestionId}
          questionText={currentQuestionText}
          value={currentValue}
          onChange={handleResponse}
          disabled={saving}
        />
      )}

      {/* Saving indicator */}
      {saving && (
        <div className="flex items-center justify-center gap-2 mt-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
}
